/**
 * MarketplacePanel - Reusable marketplace integration component
 * Hepsiburada, Amazon.tr, N11 entegrasyonları için ortak UI.
 * Trendyol mantığıyla aynı çalışır.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  Package, ShoppingBag, Users, Settings, ClipboardList,
  Upload, RefreshCw, Trash2, Download, AlertTriangle
} from 'lucide-react';
import { API_URL } from '../config/api';

const ORDERS_POLL_INTERVAL = 30000;
const PRODUCTS_POLL_INTERVAL = 60000;

const PLATFORM_CONFIGS = {
  hepsiburada: {
    name: 'Hepsiburada',
    color: '#ff6000',
    settingsFields: [
      { key: 'merchant_id', label: 'Merchant ID', placeholder: 'HB-123456', type: 'text' },
      { key: 'api_key', label: 'API Key', placeholder: 'api-key...', type: 'password' },
      { key: 'api_secret', label: 'API Secret', placeholder: 'api-secret...', type: 'password' },
    ],
    requiredFields: ['merchant_id', 'api_key', 'api_secret'],
    identifierField: 'merchant_id',
    identifierLabel: 'Merchant ID',
  },
  amazon: {
    name: 'Amazon',
    color: '#ff9900',
    settingsFields: [
      { key: 'seller_id', label: 'Seller ID', placeholder: 'A1B2C3...', type: 'text' },
      { key: 'api_key', label: 'API Key (LWA Access Token)', placeholder: 'Amzn1.application...', type: 'password' },
      { key: 'api_secret', label: 'API Secret', placeholder: 'amzn1.oa2-cs...', type: 'password' },
      { key: 'marketplace_id', label: 'Marketplace ID (Türkiye)', placeholder: 'A33AVAJ2PDY3EV', type: 'text' },
    ],
    requiredFields: ['seller_id', 'api_key', 'api_secret'],
    identifierField: 'seller_id',
    identifierLabel: 'Seller ID',
  },
  n11: {
    name: 'N11',
    color: '#7b2d8e',
    settingsFields: [
      { key: 'app_key', label: 'API Key', placeholder: 'api-key...', type: 'password' },
      { key: 'app_secret', label: 'API Secret', placeholder: 'api-secret...', type: 'password' },
    ],
    requiredFields: ['app_key', 'app_secret'],
    identifierField: 'app_key',
    identifierLabel: 'API Key',
  },
};

const MarketplacePanel = ({ platform, activeTab, user, selectedStore, stores = [], showConfirmation }) => {
  const config = PLATFORM_CONFIGS[platform] || PLATFORM_CONFIGS.hepsiburada;
  const apiPrefix = `${API_URL}/${platform}`;

  // Settings
  const [settings, setSettings] = useState({});
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  // Products
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [syncingProducts, setSyncingProducts] = useState(false);
  const [syncingInventory, setSyncingInventory] = useState(false);
  const [syncingPrices, setSyncingPrices] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  const [productFilter, setProductFilter] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [imagePopup, setImagePopup] = useState({ show: false, src: '', name: '' });

  // Orders
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [pullingOrders, setPullingOrders] = useState(false);
  const [orderFilter, setOrderFilter] = useState('all');
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkDeletingOrders, setBulkDeletingOrders] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderSearchQuery, setOrderSearchQuery] = useState('');

  // Customers
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');

  // Logs
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Polling refs
  const ordersIntervalRef = useRef(null);
  const productsIntervalRef = useRef(null);

  // ===== SETTINGS FUNCTIONS =====
  const fetchSettings = useCallback(async () => {
    setLoadingSettings(true);
    try {
      const response = await fetch(`${apiPrefix}/settings`);
      const data = await response.json();
      if (data.settings) {
        setSettings({
          ...data.settings,
          sync_enabled: data.settings.sync_enabled === 1,
          commission_percentage: data.settings.commission_percentage || 0,
          price_change_threshold: data.settings.price_change_threshold || 2,
          price_sync_interval: data.settings.price_sync_interval || 0,
          default_deduction_store_id: data.settings.default_deduction_store_id || '',
        });
      }
    } catch (error) {
      console.error(`${config.name} ayarları yüklenemedi:`, error);
    } finally {
      setLoadingSettings(false);
    }
  }, [apiPrefix, config.name]);

  const saveSettings = async () => {
    for (const field of config.requiredFields) {
      if (!settings[field]) {
        toast.error(`Tüm zorunlu alanlar doldurulmalıdır`);
        return;
      }
    }
    setSavingSettings(true);
    try {
      const response = await fetch(`${apiPrefix}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      if (response.ok) toast.success(`${config.name} ayarları kaydedildi`);
      else {
        const data = await response.json();
        toast.error(data.error || 'Ayarlar kaydedilemedi');
      }
    } catch (error) {
      toast.error('Ayarlar kaydedilemedi');
    } finally {
      setSavingSettings(false);
    }
  };

  const testConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    try {
      const response = await fetch(`${apiPrefix}/test-connection`, { method: 'POST' });
      const data = await response.json();
      setConnectionStatus(data.success ? { success: true, message: data.message } : { success: false, message: data.error });
      toast[data.success ? 'success' : 'error'](data.success ? `${config.name} bağlantısı başarılı!` : (data.error || 'Bağlantı başarısız'));
    } catch (error) {
      setConnectionStatus({ success: false, message: error.message });
      toast.error('Bağlantı test edilemedi');
    } finally {
      setTestingConnection(false);
    }
  };

  const disconnect = async () => {
    try {
      await fetch(`${apiPrefix}/auth/disconnect`, { method: 'POST' });
      setSettings(prev => {
        const updated = { ...prev, sync_enabled: false };
        config.requiredFields.forEach(f => { if (f !== config.identifierField) updated[f] = ''; });
        return updated;
      });
      toast.success(`${config.name} bağlantısı kesildi`);
    } catch (error) {
      toast.error('Bağlantı kesilemedi');
    }
  };

  // ===== PRODUCT FUNCTIONS =====
  const fetchProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const response = await fetch(`${apiPrefix}/products?page=1&limit=500`);
      const data = await response.json();
      setProducts(data.products || []);
    } catch (error) {
      console.error(`${config.name} ürünler yüklenemedi:`, error);
    } finally {
      setLoadingProducts(false);
    }
  }, [apiPrefix, config.name]);

  const syncAllProducts = async () => {
    setSyncingProducts(true);
    try {
      const response = await fetch(`${apiPrefix}/products/sync`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Sync başlatıldı');
        if (data.backgroundSync) {
          const checkProgress = setInterval(async () => {
            try {
              const statusRes = await fetch(`${apiPrefix}/products/sync-status`);
              const statusData = await statusRes.json();
              setSyncProgress(statusData.syncStatus);
              if (!statusData.syncStatus?.running) {
                clearInterval(checkProgress);
                setSyncingProducts(false);
                fetchProducts();
                toast.success(`Sync tamamlandı: ${statusData.syncStatus?.success || 0} başarılı`);
              }
            } catch { clearInterval(checkProgress); setSyncingProducts(false); }
          }, 3000);
        } else {
          setSyncingProducts(false);
          fetchProducts();
        }
      } else {
        toast.error(data.error || 'Sync başarısız');
        setSyncingProducts(false);
      }
    } catch (error) {
      toast.error('Sync başarısız');
      setSyncingProducts(false);
    }
  };

  const syncSingleProduct = async (productId) => {
    try {
      const response = await fetch(`${apiPrefix}/products/${productId}/sync`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        toast.success('Ürün sync edildi');
        fetchProducts();
      } else {
        toast.error(data.error || 'Sync başarısız');
      }
    } catch (error) {
      toast.error('Sync başarısız');
    }
  };

  const deleteProduct = async (productId) => {
    try {
      const response = await fetch(`${apiPrefix}/products/${productId}/sync`, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) { toast.success('Ürün kaldırıldı'); fetchProducts(); }
      else toast.error(data.error || 'Silme başarısız');
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const bulkDeleteProducts = async () => {
    if (selectedProducts.length === 0) return;
    setBulkDeleting(true);
    try {
      const response = await fetch(`${apiPrefix}/products/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productIds: selectedProducts })
      });
      const data = await response.json();
      if (data.success) { toast.success(data.message); setSelectedProducts([]); fetchProducts(); }
      else toast.error(data.error || 'Toplu silme başarısız');
    } catch (error) {
      toast.error('Toplu silme başarısız');
    } finally {
      setBulkDeleting(false);
    }
  };

  const syncInventory = async () => {
    setSyncingInventory(true);
    try {
      const response = await fetch(`${apiPrefix}/inventory/sync`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Stok sync başlatıldı');
        if (data.backgroundSync) {
          const checkProgress = setInterval(async () => {
            try {
              const statusRes = await fetch(`${apiPrefix}/inventory/sync-status`);
              const statusData = await statusRes.json();
              if (!statusData.syncStatus?.running) {
                clearInterval(checkProgress);
                setSyncingInventory(false);
                toast.success(`Stok sync tamamlandı: ${statusData.syncStatus?.success || 0} güncellendi`);
              }
            } catch { clearInterval(checkProgress); setSyncingInventory(false); }
          }, 3000);
        } else setSyncingInventory(false);
      } else { toast.error(data.error || 'Stok sync başarısız'); setSyncingInventory(false); }
    } catch (error) { toast.error('Stok sync başarısız'); setSyncingInventory(false); }
  };

  const syncPrices = async () => {
    setSyncingPrices(true);
    try {
      const response = await fetch(`${apiPrefix}/prices/sync`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Fiyat sync başlatıldı');
        if (data.backgroundSync) {
          const checkProgress = setInterval(async () => {
            try {
              const statusRes = await fetch(`${apiPrefix}/prices/sync-status`);
              const statusData = await statusRes.json();
              if (!statusData.syncStatus?.running) {
                clearInterval(checkProgress);
                setSyncingPrices(false);
                toast.success(`Fiyat sync tamamlandı: ${statusData.syncStatus?.success || 0} güncellendi`);
              }
            } catch { clearInterval(checkProgress); setSyncingPrices(false); }
          }, 3000);
        } else setSyncingPrices(false);
      } else { toast.error(data.error || 'Fiyat sync başarısız'); setSyncingPrices(false); }
    } catch (error) { toast.error('Fiyat sync başarısız'); setSyncingPrices(false); }
  };

  // ===== ORDER FUNCTIONS =====
  const fetchOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const response = await fetch(`${apiPrefix}/orders?page=1&limit=200`);
      const data = await response.json();
      setOrders(data.orders || []);
    } catch (error) {
      console.error(`${config.name} siparişler yüklenemedi:`, error);
    } finally {
      setLoadingOrders(false);
    }
  }, [apiPrefix, config.name]);

  const pullOrders = async () => {
    setPullingOrders(true);
    try {
      const response = await fetch(`${apiPrefix}/orders/pull`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Siparişler çekildi');
        fetchOrders();
      } else toast.error(data.error || 'Sipariş çekme başarısız');
    } catch (error) {
      toast.error('Sipariş çekme başarısız');
    } finally {
      setPullingOrders(false);
    }
  };

  const processOrder = async (orderId) => {
    try {
      const response = await fetch(`${apiPrefix}/orders/${orderId}/process`, { method: 'POST' });
      const data = await response.json();
      if (data.success) { toast.success('Sipariş işlendi'); fetchOrders(); }
      else toast.error(data.error || 'Sipariş işlenemedi');
    } catch (error) {
      toast.error('Sipariş işlenemedi');
    }
  };

  const bulkProcessOrders = async () => {
    if (selectedOrders.length === 0) return;
    setBulkProcessing(true);
    try {
      const response = await fetch(`${apiPrefix}/orders/bulk-process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderIds: selectedOrders })
      });
      const data = await response.json();
      if (data.success) { toast.success(data.message); setSelectedOrders([]); fetchOrders(); }
      else toast.error(data.error || 'Toplu işleme başarısız');
    } catch (error) {
      toast.error('Toplu işleme başarısız');
    } finally {
      setBulkProcessing(false);
    }
  };

  const bulkDeleteOrders = async () => {
    setBulkDeletingOrders(true);
    try {
      const response = await fetch(`${apiPrefix}/orders/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(selectedOrders.length > 0 ? { orderIds: selectedOrders } : { deleteAll: true })
      });
      const data = await response.json();
      if (data.success) { toast.success(data.message); setSelectedOrders([]); fetchOrders(); }
      else toast.error(data.error || 'Toplu silme başarısız');
    } catch (error) {
      toast.error('Toplu silme başarısız');
    } finally {
      setBulkDeletingOrders(false);
    }
  };

  // ===== CUSTOMER & LOG FUNCTIONS =====
  const fetchCustomers = useCallback(async () => {
    setLoadingCustomers(true);
    try {
      const response = await fetch(`${apiPrefix}/customers-from-orders`);
      const data = await response.json();
      setCustomers(data.customers || []);
    } catch (error) {
      console.error(`${config.name} müşteriler yüklenemedi:`, error);
    } finally {
      setLoadingCustomers(false);
    }
  }, [apiPrefix, config.name]);

  const fetchLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const response = await fetch(`${apiPrefix}/sync-logs?page=1&limit=100`);
      const data = await response.json();
      setLogs(data.logs || []);
    } catch (error) {
      console.error(`${config.name} loglar yüklenemedi:`, error);
    } finally {
      setLoadingLogs(false);
    }
  }, [apiPrefix, config.name]);

  // ===== EFFECTS =====
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (activeTab === 'products') fetchProducts();
    else if (activeTab === 'orders') fetchOrders();
    else if (activeTab === 'customers') fetchCustomers();
    else if (activeTab === 'logs') fetchLogs();
  }, [activeTab, fetchProducts, fetchOrders, fetchCustomers, fetchLogs]);

  // Auto polling
  useEffect(() => {
    if (activeTab === 'orders') {
      ordersIntervalRef.current = setInterval(fetchOrders, ORDERS_POLL_INTERVAL);
    }
    if (activeTab === 'products') {
      productsIntervalRef.current = setInterval(fetchProducts, PRODUCTS_POLL_INTERVAL);
    }
    return () => {
      if (ordersIntervalRef.current) clearInterval(ordersIntervalRef.current);
      if (productsIntervalRef.current) clearInterval(productsIntervalRef.current);
    };
  }, [activeTab, fetchOrders, fetchProducts]);

  // ===== FILTERS =====
  const filteredProducts = products.filter(p => {
    if (!productFilter) return true;
    const search = productFilter.toLowerCase();
    return (p.name || '').toLowerCase().includes(search) ||
           (p.barcode || '').toLowerCase().includes(search) ||
           (p.sku || '').toLowerCase().includes(search);
  });

  const filteredOrders = orders.filter(o => {
    if (orderFilter !== 'all' && o.sync_status !== orderFilter) return false;
    if (orderSearchQuery) {
      const q = orderSearchQuery.toLowerCase();
      return (o.customer_name || '').toLowerCase().includes(q) ||
             String(o[`${platform}_order_number`] || '').includes(q) ||
             String(o[`${platform}_order_id`] || '').includes(q);
    }
    return true;
  });

  const filteredCustomers = customers.filter(c => {
    if (!customerSearchQuery) return true;
    const q = customerSearchQuery.toLowerCase();
    return (c.full_name || '').toLowerCase().includes(q) ||
           (c.email || '').toLowerCase().includes(q) ||
           (c.phone || '').includes(q);
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try { return new Date(dateStr).toLocaleString('tr-TR'); } catch { return dateStr; }
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '-';
    return parseFloat(price).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' TL';
  };

  const isConnected = config.requiredFields.every(f => settings[f]);

  // ===== RENDER =====
  return (
    <>
      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="shopify-panel">
          <h2>{config.name} Bağlantı Ayarları</h2>
          <div className="settings-form">
            {/* Connection Status */}
            <div className={`connection-card ${isConnected ? 'connected' : 'disconnected'}`}>
              {isConnected ? (
                <>
                  <div className="connection-info">
                    <span className="connection-icon">&#10003;</span>
                    <div>
                      <strong>{config.name} Bağlı</strong>
                      <p>{config.identifierLabel}: {settings[config.identifierField]}</p>
                    </div>
                  </div>
                  <button className="btn btn-danger" onClick={() => showConfirmation({
                    title: 'Bağlantıyı Kes',
                    message: `${config.name} bağlantısını kesmek istediğinize emin misiniz?`,
                    type: 'danger',
                    onConfirm: disconnect
                  })}>Bağlantıyı Kes</button>
                </>
              ) : (
                <div className="connection-info">
                  <AlertTriangle size={24} color="#ef4444" />
                  <div>
                    <strong>{config.name} Bağlı Değil</strong>
                    <p>API bilgilerini girerek bağlayın</p>
                  </div>
                </div>
              )}
            </div>

            <hr className="settings-divider" />

            <div className="form-row">
              {config.settingsFields.map(field => (
                <div className="form-group" key={field.key}>
                  <label>{field.label}</label>
                  <input
                    type={field.type}
                    value={settings[field.key] || ''}
                    onChange={e => setSettings(prev => ({ ...prev, [field.key]: e.target.value }))}
                    placeholder={field.placeholder}
                  />
                </div>
              ))}
            </div>

            <hr className="settings-divider" />

            <div className="form-row">
              <div className="form-group">
                <label>Varsayılan Stok Düşüm Mağazası</label>
                <select
                  value={settings.default_deduction_store_id || ''}
                  onChange={e => setSettings(prev => ({ ...prev, default_deduction_store_id: e.target.value }))}
                >
                  <option value="">Seçiniz</option>
                  {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Komisyon Oranı (%)</label>
                <input
                  type="number" step="0.01" min="0"
                  value={settings.commission_percentage || 0}
                  onChange={e => setSettings(prev => ({ ...prev, commission_percentage: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Fiyat Değişim Eşiği (%)</label>
                <input
                  type="number" step="0.1" min="0"
                  value={settings.price_change_threshold || 2}
                  onChange={e => setSettings(prev => ({ ...prev, price_change_threshold: parseFloat(e.target.value) || 2 }))}
                />
              </div>
              <div className="form-group">
                <label>Otomatik Fiyat Sync (dakika, 0=kapalı)</label>
                <input
                  type="number" min="0"
                  value={settings.price_sync_interval || 0}
                  onChange={e => setSettings(prev => ({ ...prev, price_sync_interval: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>

            <div
              onClick={() => setSettings(prev => ({ ...prev, sync_enabled: !prev.sync_enabled }))}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '12px', marginTop: '8px',
                background: settings.sync_enabled ? 'rgba(16,185,129,0.1)' : '#f1f5f9',
                padding: '10px 20px', borderRadius: '24px', cursor: 'pointer', transition: 'all 0.2s',
                border: settings.sync_enabled ? '2px solid #10b981' : '2px solid #e2e8f0'
              }}
            >
              <div style={{
                width: '40px', height: '22px', borderRadius: '11px', position: 'relative', transition: 'all 0.3s',
                background: settings.sync_enabled ? '#10b981' : '#cbd5e1'
              }}>
                <div style={{
                  width: '18px', height: '18px', borderRadius: '50%', background: '#fff', position: 'absolute',
                  top: '2px', left: settings.sync_enabled ? '20px' : '2px', transition: 'all 0.3s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }} />
              </div>
              <span style={{ fontWeight: 600, fontSize: '0.9rem', color: settings.sync_enabled ? '#059669' : '#64748b' }}>
                Senkronizasyon {settings.sync_enabled ? 'Aktif' : 'Kapalı'}
              </span>
            </div>

            <div className="settings-actions">
              <button className="btn btn-primary" onClick={saveSettings} disabled={savingSettings}>
                {savingSettings ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
              </button>
              <button className="btn btn-secondary" onClick={testConnection} disabled={testingConnection}>
                {testingConnection ? 'Test Ediliyor...' : 'Bağlantıyı Test Et'}
              </button>
            </div>

            {connectionStatus && (
              <div className={`connection-result ${connectionStatus.success ? 'success' : 'error'}`}>
                {connectionStatus.success ? '✓' : '✗'} {connectionStatus.message}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Products Tab */}
      {activeTab === 'products' && (
        <div className="shopify-panel">
          <div className="panel-header">
            <h2>{config.name} Ürünleri</h2>
            <div className="panel-actions">
              <button className="btn-sm btn-secondary" onClick={fetchProducts} disabled={loadingProducts}>
                <RefreshCw size={14} className={loadingProducts ? 'spin' : ''} /> Yenile
              </button>
              <button className="btn-sm btn-primary" onClick={syncAllProducts} disabled={syncingProducts}>
                <Upload size={14} /> {syncingProducts ? 'Sync Devam Ediyor...' : 'Tümünü Sync Et'}
              </button>
              <button className="btn-sm btn-success" onClick={syncInventory} disabled={syncingInventory}>
                <Package size={14} /> {syncingInventory ? 'Stok Güncelleniyor...' : 'Stok Eşitle'}
              </button>
              <button className="btn-sm btn-warning" onClick={syncPrices} disabled={syncingPrices}>
                {syncingPrices ? 'Fiyat Güncelleniyor...' : 'Fiyat Eşitle'}
              </button>
            </div>
          </div>

          {syncProgress && syncProgress.running && (
            <div className="sync-progress-bar">
              <div className="progress-info">
                <span>Sync: {syncProgress.success + syncProgress.failed} / {syncProgress.total}</span>
                <span>{syncProgress.success} başarılı, {syncProgress.failed} hata</span>
              </div>
              <div className="progress-track">
                <div className="progress-fill" style={{
                  width: `${syncProgress.total > 0 ? ((syncProgress.success + syncProgress.failed) / syncProgress.total * 100) : 0}%`
                }} />
              </div>
            </div>
          )}

          {selectedProducts.length > 0 && (
            <div className="bulk-actions-bar">
              <span>{selectedProducts.length} ürün seçili</span>
              <button className="btn-sm btn-danger" onClick={() => showConfirmation({
                title: 'Toplu Silme',
                message: `${selectedProducts.length} ürünü ${config.name}'dan kaldırmak istediğinize emin misiniz?`,
                type: 'danger',
                onConfirm: bulkDeleteProducts
              })} disabled={bulkDeleting}>
                <Trash2 size={14} /> Seçilenleri Kaldır
              </button>
              <button className="btn-sm" onClick={() => setSelectedProducts([])}>Seçimi Kaldır</button>
            </div>
          )}

          <div className="filter-bar">
            <input
              type="text" placeholder="Ürün ara (ad, barkod, SKU)..."
              value={productFilter}
              onChange={e => setProductFilter(e.target.value)}
              className="filter-input"
            />
          </div>

          {loadingProducts ? (
            <div className="loading">Yükleniyor...</div>
          ) : (
            <div className="products-table-wrapper">
              <table className="shopify-table">
                <thead>
                  <tr>
                    <th><input type="checkbox"
                      checked={filteredProducts.length > 0 && selectedProducts.length === filteredProducts.length}
                      onChange={e => setSelectedProducts(e.target.checked ? filteredProducts.map(p => p.id) : [])}
                    /></th>
                    <th>Resim</th>
                    <th>Ürün</th>
                    <th>Barkod</th>
                    <th>Fiyat</th>
                    <th>Stok</th>
                    <th>Durum</th>
                    <th>Son Sync</th>
                    <th>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.length === 0 ? (
                    <tr><td colSpan="9" className="empty-state">
                      {productFilter ? 'Arama sonucu bulunamadı' : `${config.name}'da aktif ürün yok. Ürünler sayfasından ürünleri ${config.name} aktif yapın.`}
                    </td></tr>
                  ) : filteredProducts.map(product => (
                    <tr key={product.id}>
                      <td><input type="checkbox"
                        checked={selectedProducts.includes(product.id)}
                        onChange={e => setSelectedProducts(prev =>
                          e.target.checked ? [...prev, product.id] : prev.filter(id => id !== product.id)
                        )}
                      /></td>
                      <td>
                        {product.image ? (
                          <img
                            src={product.image.startsWith('http') ? product.image : `${API_URL.replace('/api', '')}/uploads/${product.image}`}
                            alt={product.name}
                            className="product-thumb"
                            onClick={() => setImagePopup({
                              show: true,
                              src: product.image.startsWith('http') ? product.image : `${API_URL.replace('/api', '')}/uploads/${product.image}`,
                              name: product.name
                            })}
                          />
                        ) : <div className="no-image">-</div>}
                      </td>
                      <td className="product-name">{product.name}</td>
                      <td className="product-barcode">{product.barcode || '-'}</td>
                      <td className="product-price">{formatPrice(product.sell_price)}</td>
                      <td className="product-stock">{product.total_stock || 0}</td>
                      <td>
                        <span className={`status-badge ${product.sync_status || 'pending'}`}>
                          {product.sync_status === 'synced' ? 'Senkron' :
                           product.sync_status === 'processing' ? 'İşleniyor' :
                           product.sync_status === 'error' ? 'Hata' : 'Bekliyor'}
                        </span>
                        {product.sync_error && <small className="sync-error-text" title={product.sync_error}>!</small>}
                      </td>
                      <td className="date-cell">{formatDate(product.last_synced_at)}</td>
                      <td className="actions-cell">
                        <button className="btn-xs btn-primary" onClick={() => syncSingleProduct(product.id)} title="Sync Et">
                          <RefreshCw size={12} />
                        </button>
                        <button
                          className="btn-xs btn-danger"
                          style={{ fontSize: '13px', fontWeight: 700 }}
                          onClick={(e) => { e.stopPropagation(); showConfirmation({
                            title: 'Ürünü Kaldır',
                            message: `"${product.name}" ürününü ${config.name}'dan kaldırmak istediğinize emin misiniz?`,
                            type: 'danger',
                            onConfirm: () => deleteProduct(product.id)
                          }); }}
                          title="Kaldır"
                        >✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div className="shopify-panel">
          <div className="panel-header">
            <h2>{config.name} Siparişleri</h2>
            <div className="panel-actions">
              <button className="btn-sm btn-secondary" onClick={fetchOrders} disabled={loadingOrders}>
                <RefreshCw size={14} className={loadingOrders ? 'spin' : ''} /> Yenile
              </button>
              <button className="btn-sm btn-primary" onClick={pullOrders} disabled={pullingOrders}>
                <Download size={14} /> {pullingOrders ? 'Çekiliyor...' : 'Siparişleri Çek'}
              </button>
            </div>
          </div>

          {selectedOrders.length > 0 && (
            <div className="bulk-actions-bar">
              <span>{selectedOrders.length} sipariş seçili</span>
              <button className="btn-sm btn-primary" onClick={bulkProcessOrders} disabled={bulkProcessing}>
                {bulkProcessing ? 'İşleniyor...' : 'Seçilenleri İşle'}
              </button>
              <button className="btn-sm btn-danger" onClick={() => showConfirmation({
                title: 'Toplu Silme',
                message: `${selectedOrders.length} siparişi silmek istediğinize emin misiniz?`,
                type: 'danger',
                onConfirm: bulkDeleteOrders
              })} disabled={bulkDeletingOrders}>
                <Trash2 size={14} /> Seçilenleri Sil
              </button>
              <button className="btn-sm" onClick={() => setSelectedOrders([])}>Seçimi Kaldır</button>
            </div>
          )}

          <div className="filter-bar">
            <select value={orderFilter} onChange={e => setOrderFilter(e.target.value)} className="filter-select">
              <option value="all">Tüm Siparişler</option>
              <option value="pending">Bekleyen</option>
              <option value="synced">İşlenmiş</option>
              <option value="error">Hatalı</option>
            </select>
            <input
              type="text" placeholder="Sipariş ara..."
              value={orderSearchQuery}
              onChange={e => setOrderSearchQuery(e.target.value)}
              className="filter-input"
            />
          </div>

          {loadingOrders ? (
            <div className="loading">Yükleniyor...</div>
          ) : (
            <div className="products-table-wrapper">
              <table className="shopify-table">
                <thead>
                  <tr>
                    <th><input type="checkbox"
                      checked={filteredOrders.length > 0 && selectedOrders.length === filteredOrders.length}
                      onChange={e => setSelectedOrders(e.target.checked ? filteredOrders.map(o => o.id) : [])}
                    /></th>
                    <th>Sipariş No</th>
                    <th>Müşteri</th>
                    <th>Tutar</th>
                    <th>Durum</th>
                    <th>Tarih</th>
                    <th>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.length === 0 ? (
                    <tr><td colSpan="7" className="empty-state">Sipariş bulunamadı</td></tr>
                  ) : filteredOrders.map(order => (
                    <tr key={order.id} onClick={() => setSelectedOrder(selectedOrder?.id === order.id ? null : order)}>
                      <td onClick={e => e.stopPropagation()}>
                        <input type="checkbox"
                          checked={selectedOrders.includes(order.id)}
                          onChange={e => setSelectedOrders(prev =>
                            e.target.checked ? [...prev, order.id] : prev.filter(id => id !== order.id)
                          )}
                        />
                      </td>
                      <td className="order-number">#{order[`${platform}_order_number`] || order[`${platform}_order_id`] || order.id}</td>
                      <td>{order.customer_name || '-'}</td>
                      <td className="product-price">{formatPrice(order.total_price)}</td>
                      <td>
                        <span className={`status-badge ${order.sync_status}`}>
                          {order.sync_status === 'synced' ? 'İşlenmiş' :
                           order.sync_status === 'processing' ? 'İşleniyor' :
                           order.sync_status === 'error' ? 'Hata' : 'Bekliyor'}
                        </span>
                      </td>
                      <td className="date-cell">{formatDate(order.platform_created_at || order.created_at)}</td>
                      <td className="actions-cell" onClick={e => e.stopPropagation()}>
                        {order.sync_status !== 'synced' && (
                          <button className="btn-xs btn-primary" onClick={() => processOrder(order.id)} title="İşle">
                            <Upload size={12} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Order Detail Modal */}
          {selectedOrder && (
            <div className="order-detail-panel">
              <div className="order-detail-header">
                <h3>Sipariş #{selectedOrder[`${platform}_order_number`] || selectedOrder.id}</h3>
                <button className="btn-sm" onClick={() => setSelectedOrder(null)}>Kapat</button>
              </div>
              <div className="order-detail-body">
                <div className="detail-row"><strong>Müşteri:</strong> {selectedOrder.customer_name}</div>
                <div className="detail-row"><strong>E-posta:</strong> {selectedOrder.customer_email || '-'}</div>
                <div className="detail-row"><strong>Telefon:</strong> {selectedOrder.customer_phone || '-'}</div>
                <div className="detail-row"><strong>Tutar:</strong> {formatPrice(selectedOrder.total_price)}</div>
                <div className="detail-row"><strong>Durum:</strong> {selectedOrder.sync_status}</div>
                <div className="detail-row"><strong>Sipariş Durumu:</strong> {selectedOrder.order_status || '-'}</div>
                <div className="detail-row"><strong>Tarih:</strong> {formatDate(selectedOrder.platform_created_at)}</div>
                {selectedOrder.sync_error && (
                  <div className="detail-row error"><strong>Hata:</strong> {selectedOrder.sync_error}</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Customers Tab */}
      {activeTab === 'customers' && (
        <div className="shopify-panel">
          <div className="panel-header">
            <h2>{config.name} Müşterileri</h2>
            <div className="panel-actions">
              <button className="btn-sm btn-secondary" onClick={fetchCustomers} disabled={loadingCustomers}>
                <RefreshCw size={14} className={loadingCustomers ? 'spin' : ''} /> Yenile
              </button>
            </div>
          </div>

          <div className="filter-bar">
            <input
              type="text" placeholder="Müşteri ara..."
              value={customerSearchQuery}
              onChange={e => setCustomerSearchQuery(e.target.value)}
              className="filter-input"
            />
          </div>

          {loadingCustomers ? (
            <div className="loading">Yükleniyor...</div>
          ) : (
            <div className="products-table-wrapper">
              <table className="shopify-table">
                <thead>
                  <tr>
                    <th>İsim</th>
                    <th>E-posta</th>
                    <th>Telefon</th>
                    <th>Sipariş</th>
                    <th>Toplam Harcama</th>
                    <th>Son Sipariş</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.length === 0 ? (
                    <tr><td colSpan="6" className="empty-state">Müşteri bulunamadı</td></tr>
                  ) : filteredCustomers.map(customer => (
                    <tr key={customer.id}>
                      <td>{customer.full_name}</td>
                      <td>{customer.email || '-'}</td>
                      <td>{customer.phone || '-'}</td>
                      <td>{customer.orders_count}</td>
                      <td className="product-price">{formatPrice(customer.total_spent)}</td>
                      <td className="date-cell">{formatDate(customer.last_order_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <div className="shopify-panel">
          <div className="panel-header">
            <h2>{config.name} Sync Logları</h2>
            <div className="panel-actions">
              <button className="btn-sm btn-secondary" onClick={fetchLogs} disabled={loadingLogs}>
                <RefreshCw size={14} className={loadingLogs ? 'spin' : ''} /> Yenile
              </button>
            </div>
          </div>

          {loadingLogs ? (
            <div className="loading">Yükleniyor...</div>
          ) : (
            <div className="products-table-wrapper">
              <table className="shopify-table">
                <thead>
                  <tr>
                    <th>Tarih</th>
                    <th>Tip</th>
                    <th>Durum</th>
                    <th>Mesaj</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr><td colSpan="4" className="empty-state">Henüz log yok</td></tr>
                  ) : logs.map(log => (
                    <tr key={log.id}>
                      <td className="date-cell">{formatDate(log.created_at)}</td>
                      <td>
                        <span className={`log-type-badge ${log.sync_type}`}>
                          {log.sync_type === 'product_push' ? 'Ürün Gönder' :
                           log.sync_type === 'product_update' ? 'Ürün Güncelle' :
                           log.sync_type === 'inventory_update' ? 'Stok Güncelle' :
                           log.sync_type === 'price_update' ? 'Fiyat Güncelle' :
                           log.sync_type === 'order_pull' ? 'Sipariş Çek' :
                           log.sync_type === 'product_delete' ? 'Ürün Sil' : log.sync_type}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${log.status}`}>
                          {log.status === 'success' ? 'Başarılı' :
                           log.status === 'error' ? 'Hata' :
                           log.status === 'partial' ? 'Kısmi' : log.status}
                        </span>
                      </td>
                      <td className="log-message">{log.message || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Image Popup */}
      {imagePopup.show && (
        <div className="image-popup-overlay" onClick={() => setImagePopup({ show: false, src: '', name: '' })}>
          <div className="image-popup-content" onClick={e => e.stopPropagation()}>
            <img src={imagePopup.src} alt={imagePopup.name} />
            <button className="image-popup-close" onClick={() => setImagePopup({ show: false, src: '', name: '' })}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

export default MarketplacePanel;
